import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { LessonObject } from '@vrlearning/lesson-schema';

/**
 * The bones of a skeleton, built from shapes.
 *
 * Hand-built rather than a scanned model, by Pieter's choice (ADR-025): no
 * download, no licence to carry. So this aims for what shapes can do well --
 * **arranged like a body** rather than scanned from one. Real proportions for
 * a 1.70 m adult, a spine with its S-curve and twenty-four vertebrae, twelve
 * pairs of ribs that slope down to a sternum through cartilage, a pelvis with
 * the hole between its rami, hands with their finger bones and feet with an
 * arch. Joints meet, and bones.test.ts holds them to it.
 *
 * **Right side authored, left side mirrored.** The lesson builds both sides
 * from the same names, and a femur whose neck angles in towards the hip, a
 * thumb on the outside of the hand, a shoulder blade on the back, is not
 * symmetrical. So every paired bone is written once for the right and mirrored
 * for the left, which also makes the two sides exact mirrors of each other --
 * tested, because built identically the left femur's neck pointed away from
 * its hip.
 *
 * **One mesh per bone**, merged. Two hundred vertebra processes and finger
 * joints as separate meshes, with a faint outline of every one during the
 * building step, would be several hundred draw calls in a headset. Merged it
 * is two or three per bone. Cartilage is a separate, bluer mesh, because the
 * ribs joining the sternum through cartilage is something worth seeing.
 *
 * Coordinates: +x is the bone's lateral side (away from the body's midline
 * for a right bone), +y is up, +z is the front. Every bone is built about its
 * lesson `pos`, so the lesson says where its middle is (ADR-025).
 */

type BuildContext = { id: string; name: string; object: LessonObject };
type BuiltBone = {
  body: THREE.Mesh;
  extras?: THREE.Object3D[];
  labelScale?: number;
  labelHeight?: number;
  nameplate?: { at: [number, number, number]; side: -1 | 0 | 1 };
};

/**
 * Where a bone's name goes.
 *
 * Beside the bone at its own height, never above it: the tops of the spine,
 * the ribs, the shoulder blades and the skull are all within a hand's width of
 * each other, and "above the top" stacked six names on the skull. A limb's
 * name goes on its outside. The middle bones alternate sides and stand clear
 * of the arms, so two names never share a place.
 */
type Nameplate = { lateral: true } | { side: -1 | 1; height: number };
type V3 = readonly [number, number, number];

/** White, as a teaching skeleton is. */
const BONE = 0xf6f4f0;
/** Cartilage: the same family, a shade cooler, so it reads as a different tissue. */
const CARTILAGE = 0xc9d6df;
/** Hollows -- eye sockets, the nose -- are what make a skull read as a skull. */
const HOLLOW = 0x2a241e;

// --- building blocks ---------------------------------------------------------

/**
 * Geometry collected in one bone's space, then merged. Pieces are converted
 * to non-indexed geometry first, because the built-in shapes are not all
 * indexed the same way and a merge refuses to mix them.
 */
class Pieces {
  readonly #all: THREE.BufferGeometry[] = [];

  add(geometry: THREE.BufferGeometry): this {
    this.#all.push(geometry.index ? geometry.toNonIndexed() : geometry);
    return this;
  }

  get empty(): boolean {
    return this.#all.length === 0;
  }

  merged(): THREE.BufferGeometry {
    const merged = mergeGeometries(this.#all, false);
    if (!merged) throw new Error('bone pieces could not be merged');
    return merged;
  }
}

const v = (p: V3): THREE.Vector3 => new THREE.Vector3(...p);

/** Lays a shape built along +y so that it runs from `a` to `b`. */
function along(geometry: THREE.BufferGeometry, a: V3, b: V3): THREE.BufferGeometry {
  const from = v(a);
  const to = v(b);
  const direction = to.clone().sub(from).normalize();
  const turn = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
  const middle = from.add(to).multiplyScalar(0.5);
  return geometry.applyMatrix4(
    new THREE.Matrix4().compose(middle, turn, new THREE.Vector3(1, 1, 1)),
  );
}

const lengthOf = (a: V3, b: V3): number => v(a).distanceTo(v(b));

/** A rounded rod from `a` to `b`: a finger bone, a rib process, a clavicle end. */
function rod(a: V3, b: V3, radius: number): THREE.BufferGeometry {
  return along(new THREE.CapsuleGeometry(radius, lengthOf(a, b), 3, 8), a, b);
}

/**
 * A long bone's shaft from `a` to `b`: wide at both ends, waisted in the
 * middle -- the silhouette a cylinder gets wrong.
 */
function shaft(a: V3, b: V3, radius: number, flare = 1.5): THREE.BufferGeometry {
  const length = lengthOf(a, b);
  const profile: [number, number][] = [
    [0.001, -0.5],
    [flare * 0.95, -0.48],
    [flare, -0.44],
    [1.05, -0.3],
    [0.92, -0.05],
    [0.95, 0.2],
    [1.1, 0.35],
    [flare, 0.45],
    [flare * 0.95, 0.49],
    [0.001, 0.5],
  ];
  const lathe = new THREE.LatheGeometry(
    profile.map(([r, y]) => new THREE.Vector2(r * radius, y * length)),
    12,
  );
  return along(lathe, a, b);
}

/** A rounded lump: a condyle, a head, a knuckle. */
function ball(
  at: V3,
  radius: number,
  scale: V3 = [1, 1, 1],
  turn: V3 = [0, 0, 0],
): THREE.BufferGeometry {
  const sphere = new THREE.SphereGeometry(radius, 12, 9);
  return sphere.applyMatrix4(
    new THREE.Matrix4().compose(
      v(at),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(...turn)),
      v(scale),
    ),
  );
}

/** A bone that bends: a rib, a clavicle, a crest. */
function curve(points: readonly V3[], radius: number, segments = 18): THREE.BufferGeometry {
  const path = new THREE.CatmullRomCurve3(points.map(v));
  return new THREE.TubeGeometry(path, segments, radius, 7, false);
}

/** A short cylinder standing on its end: a vertebral body. */
function disc(at: V3, radius: number, height: number, scale: V3 = [1, 1, 1]): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(radius, radius * 1.04, height, 12).applyMatrix4(
    new THREE.Matrix4().compose(v(at), new THREE.Quaternion(), v(scale)),
  );
}

/** A ring: the rim of a socket or an eye. `turn` sets which way it faces. */
function ring(
  at: V3,
  radius: number,
  tube: number,
  turn: V3,
  arc = Math.PI * 2,
): THREE.BufferGeometry {
  return new THREE.TorusGeometry(radius, tube, 6, 20, arc).applyMatrix4(
    new THREE.Matrix4().compose(
      v(at),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(...turn)),
      new THREE.Vector3(1, 1, 1),
    ),
  );
}

/**
 * Mirrors a right bone into a left one.
 *
 * Negating x turns every triangle inside out, so the winding is swapped back
 * afterwards; without that the left side renders as its own inside.
 */
function mirrorX(geometry: THREE.BufferGeometry): void {
  geometry.scale(-1, 1, 1);
  for (const attribute of Object.values(geometry.attributes)) {
    const size = attribute.itemSize;
    const values = attribute.array;
    for (let vertex = 0; vertex + 2 < attribute.count; vertex += 3) {
      for (let c = 0; c < size; c++) {
        const second = (vertex + 1) * size + c;
        const third = (vertex + 2) * size + c;
        const swap = values[second]!;
        values[second] = values[third]!;
        values[third] = swap;
      }
    }
    attribute.needsUpdate = true;
  }
}

function material(color: number): THREE.MeshStandardMaterial {
  // Both sides drawn: shoulder blades and pelvic blades are thin shells, and
  // a thin shell seen from behind should not vanish.
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.58,
    metalness: 0,
    side: THREE.DoubleSide,
  });
}

/**
 * Turns a bone's pieces into meshes: the bone itself, and its cartilage and
 * hollows if it has any. Scaled by the lesson's `radius` so the same names
 * build a child-sized skeleton or a giant one, and mirrored for the left.
 */
function finish(
  bone: Pieces,
  options: {
    cartilage?: Pieces;
    hollows?: Pieces;
    mirrored: boolean;
    scale: number;
    nameplate?: Nameplate;
  },
): BuiltBone {
  const shape = (pieces: Pieces): THREE.BufferGeometry => {
    const geometry = pieces.merged();
    geometry.scale(options.scale, options.scale, options.scale);
    if (options.mirrored) mirrorX(geometry);
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    return geometry;
  };

  const bodyGeometry = shape(bone);
  const body = new THREE.Mesh(bodyGeometry, material(BONE));
  const extras: THREE.Object3D[] = [];

  if (options.cartilage && !options.cartilage.empty) {
    extras.push(new THREE.Mesh(shape(options.cartilage), material(CARTILAGE)));
  }
  if (options.hollows && !options.hollows.empty) {
    extras.push(
      new THREE.Mesh(shape(options.hollows), new THREE.MeshBasicMaterial({ color: HOLLOW })),
    );
  }

  const bounds = bodyGeometry.boundingBox!;
  const plate = options.nameplate ?? { lateral: true };
  let nameplate: BuiltBone['nameplate'];
  if ('lateral' in plate) {
    const side = options.mirrored ? -1 : 1;
    const edge = side > 0 ? bounds.max.x : bounds.min.x;
    nameplate = {
      at: [edge + side * 0.025, (bounds.min.y + bounds.max.y) / 2, 0.03 * options.scale],
      side,
    };
  } else {
    // Clear of the arms, which hang about a quarter of a metre out.
    nameplate = {
      at: [plate.side * 0.28 * options.scale, plate.height * options.scale, 0.04 * options.scale],
      side: plate.side,
    };
  }

  return {
    body,
    extras,
    labelHeight: bounds.max.y + 0.05,
    // Bone-sized names: the planet-sized default is taller than a hand.
    labelScale: 0.3 * options.scale,
    nameplate,
  };
}

// --- the bones ----------------------------------------------------------------

/**
 * The skull: a cranium longer than it is wide, a brow, eye sockets with rims,
 * the nasal opening, cheekbones, two rows of teeth, and the mandible hung on
 * its two rami -- the one bone of the head a child can move on themselves.
 */
function skull(): { bone: Pieces; hollows: Pieces } {
  const bone = new Pieces();
  const hollows = new Pieces();

  bone.add(ball([0, 0.028, -0.012], 0.09, [0.86, 0.95, 1.08]));
  bone.add(rod([-0.045, 0.004, 0.074], [0.045, 0.004, 0.074], 0.011));

  for (const side of [-1, 1]) {
    hollows.add(ball([side * 0.031, -0.02, 0.07], 0.02, [1.1, 0.95, 0.55]));
    bone.add(ring([side * 0.031, -0.02, 0.078], 0.021, 0.005, [0, 0, 0]));
    bone.add(rod([side * 0.046, -0.04, 0.064], [side * 0.068, -0.035, 0.004], 0.008));
    bone.add(ball([side * 0.056, -0.047, -0.042], 0.011));
    bone.add(rod([side * 0.047, -0.097, -0.005], [side * 0.052, -0.048, -0.018], 0.009));
  }

  bone.add(ball([0, -0.055, 0.045], 0.045, [0.95, 0.8, 0.9]));
  hollows.add(ball([0, -0.046, 0.083], 0.015, [0.75, 1.2, 0.45]));
  bone.add(ring([0, -0.079, 0.048], 0.031, 0.0065, [Math.PI / 2, 0, 0], Math.PI));
  bone.add(ring([0, -0.087, 0.046], 0.03, 0.006, [Math.PI / 2, 0, 0], Math.PI));
  bone.add(ring([0, -0.094, 0.034], 0.041, 0.01, [Math.PI / 2, 0, 0], Math.PI * 1.04));
  bone.add(ball([0, -0.1, 0.074], 0.012, [1.4, 1, 1]));

  return { bone, hollows };
}

/**
 * The spine, atlas to fifth lumbar: 7 cervical, 12 thoracic, 5 lumbar, each a
 * body with a spinous process behind it and a transverse process either side,
 * getting bigger towards the base, which carries the most weight.
 *
 * With the curves a real one has -- forward at the neck, back through the
 * chest, forward again at the small of the back. A straight stack is the
 * commonest way to draw a spine wrongly, and the curve is also why the ribs
 * sit where they do.
 */
function spine(): Pieces {
  const bone = new Pieces();
  const top = 0.285;
  const bottom = -0.285;
  const regions = [
    {
      count: 7,
      height: 0.11,
      radius: [0.009, 0.011],
      spinous: 0.018,
      drop: 0.006,
      transverse: 0.012,
    },
    {
      count: 12,
      height: 0.28,
      radius: [0.012, 0.016],
      spinous: 0.03,
      drop: 0.022,
      transverse: 0.02,
    },
    {
      count: 5,
      height: 0.18,
      radius: [0.018, 0.022],
      spinous: 0.026,
      drop: 0.004,
      transverse: 0.027,
    },
  ];

  const keys: V3[] = [
    [0, top, 0],
    [0, top - 0.07, 0.02],
    [0, top - 0.24, -0.017],
    [0, bottom + 0.08, 0.014],
    [0, bottom, 0],
  ];
  const bend = new THREE.CatmullRomCurve3(keys.map(v));
  const zAt = (y: number): number => {
    const t = THREE.MathUtils.clamp((top - y) / (top - bottom), 0, 1);
    return bend.getPoint(t).z;
  };

  let y = top;
  for (const region of regions) {
    const step = region.height / region.count;
    for (let i = 0; i < region.count; i++) {
      const t = i / Math.max(region.count - 1, 1);
      const r = THREE.MathUtils.lerp(region.radius[0]!, region.radius[1]!, t);
      const centre = y - step / 2;
      const z = zAt(centre);

      bone.add(disc([0, centre, z], r, step * 0.72, [1, 1, 0.82]));
      bone.add(
        rod([0, centre, z - r * 0.8], [0, centre - region.drop, z - r - region.spinous], r * 0.3),
      );
      for (const side of [-1, 1]) {
        bone.add(
          rod(
            [side * r * 0.6, centre, z - r * 0.5],
            [side * (r + region.transverse), centre + 0.002, z - r * 0.9],
            r * 0.24,
          ),
        );
      }
      y -= step;
    }
  }
  return bone;
}

/**
 * The ribcage: twelve pairs, each leaving its vertebra backwards and outwards,
 * sweeping round and down to the front. The first seven reach the sternum
 * through their own cartilage; eight to ten join the cartilage above them,
 * making the arch under the chest; eleven and twelve float.
 *
 * Widest around the eighth pair, narrow at the collar -- a barrel of equal
 * hoops is the other common mistake.
 */
function ribcage(): { bone: Pieces; cartilage: Pieces } {
  const bone = new Pieces();
  const cartilage = new Pieces();

  // Where each rib leaves its thoracic vertebra, in the ribcage's own space.
  const headY = (k: number): number => 0.168 - (k - 1) * 0.0233;
  const halfWidth = [
    0.058, 0.082, 0.098, 0.11, 0.12, 0.128, 0.132, 0.134, 0.132, 0.126, 0.112, 0.092,
  ];
  // Where the first seven meet the sternum, from the notch down.
  const sternalY = (k: number): number => 0.11 - (k - 1) * 0.0235;
  const back = -0.07;
  const front = 0.085;

  for (let k = 1; k <= 12; k++) {
    const w = halfWidth[k - 1]!;
    const y = headY(k);
    const thickness = k <= 2 ? 0.0055 : 0.0048;

    for (const side of [-1, 1]) {
      const x = (value: number): number => side * value;
      if (k >= 11) {
        // Floating: short, ending round the side with nothing in front.
        bone.add(
          curve(
            [
              [x(0.012), y, back],
              [x(0.05), y - 0.012, back - 0.012],
              [x(w * 0.95), y - 0.05, -0.02],
              [x(w * 0.8), y - 0.075, 0.02],
            ],
            thickness * 0.9,
          ),
        );
        continue;
      }

      const endY = k <= 7 ? sternalY(k) : y - 0.12 - (k - 8) * 0.012;
      const endX = k <= 7 ? w * 0.42 : w * 0.62;
      bone.add(
        curve(
          [
            [x(0.012), y, back],
            [x(0.052), y - 0.012, back - 0.012],
            [x(w), y - 0.04 - (endY < y ? (y - endY) * 0.2 : 0), -0.005],
            [x(w * 0.78), (y + endY) / 2 - 0.02, 0.055],
            [x(endX), endY - 0.004, front - 0.012],
          ],
          thickness,
        ),
      );

      if (k <= 7) {
        cartilage.add(
          curve(
            [
              [x(endX), endY - 0.004, front - 0.012],
              [x((endX + 0.018) / 2), endY - 0.002, front - 0.002],
              [x(0.016), endY, front],
            ],
            thickness * 1.1,
            8,
          ),
        );
      } else {
        // Up to the cartilage of the rib above: the costal arch.
        cartilage.add(
          curve(
            [
              [x(endX), endY - 0.004, front - 0.012],
              [x(endX * 0.7), endY + 0.03, front - 0.005],
              [x(w * 0.3), sternalY(7) - 0.02, front],
            ],
            thickness * 1.05,
            8,
          ),
        );
      }
    }
  }

  // The sternum: manubrium, body, and the small xiphoid at the bottom.
  bone.add(ball([0, 0.118, front], 0.024, [1.05, 0.9, 0.3]));
  bone.add(ball([0, 0.035, front + 0.002], 0.019, [0.9, 3.2, 0.28]));
  bone.add(rod([0, -0.045, front], [0, -0.07, front - 0.006], 0.005));

  return { bone, cartilage };
}

/**
 * The pelvis: two hip bones with flared blades, the sacrum and coccyx behind,
 * the sockets the femurs sit in, and the pubic and ischial rami below -- with
 * the opening between them that makes a pelvis read as a pelvis and not a
 * bowl.
 */
function pelvis(): Pieces {
  const bone = new Pieces();

  // Sacrum: fused vertebrae narrowing and curving back into the coccyx.
  const sacralPath: V3[] = [
    [0, 0.055, -0.035],
    [0, 0.0, -0.052],
    [0, -0.05, -0.068],
    [0, -0.085, -0.066],
  ];
  const sacrum = new THREE.CatmullRomCurve3(sacralPath.map(v));
  for (let i = 0; i < 7; i++) {
    const t = i / 6;
    const at = sacrum.getPoint(t);
    const width = THREE.MathUtils.lerp(0.05, 0.008, t);
    bone.add(ball([at.x, at.y, at.z], width, [1, 0.42, 0.45]));
  }

  for (const side of [-1, 1]) {
    const x = (value: number): number => side * value;

    // The blade: a curved shell that flares *outwards* towards the top, the
    // way a bowl does -- the lower part of a sphere, on this side. A flattened
    // lump read as a round disc from the front, which is the one thing a
    // pelvis does not look like.
    const reach = 0.14;
    const across = 0.95;
    const blade = new THREE.SphereGeometry(
      reach,
      14,
      8,
      (side > 0 ? Math.PI : 0) - across,
      across * 2,
      Math.PI / 2,
      0.75,
    );
    bone.add(
      blade.applyMatrix4(
        new THREE.Matrix4().compose(
          new THREE.Vector3(0, 0.105, 0),
          new THREE.Quaternion(),
          new THREE.Vector3(1, 1, 0.6),
        ),
      ),
    );
    // Its rim, the crest you feel at your hip, following the shell's edge.
    const crest: V3[] = [];
    for (let i = 0; i <= 8; i++) {
      const a = -across + (i / 8) * across * 2;
      crest.push([x(reach * Math.cos(a)), 0.105, 0.6 * reach * Math.sin(a)]);
    }
    bone.add(curve(crest, 0.006, 16));
    // Down from the blade to the socket: the body of the hip bone.
    bone.add(rod([x(0.1), 0.02, 0.0], [x(0.085), -0.045, 0.02], 0.017));
    // The socket, and its rim facing out and forwards.
    bone.add(ball([x(0.083), -0.06, 0.025], 0.02, [0.7, 1, 1]));
    bone.add(ring([x(0.09), -0.06, 0.028], 0.024, 0.0065, [0, side * 1.15, 0]));
    // Pubic rami to the front, ischium down to the sitting bone.
    bone.add(
      curve(
        [
          [x(0.075), -0.052, 0.05],
          [x(0.045), -0.065, 0.075],
          [x(0.012), -0.08, 0.085],
        ],
        0.0075,
        10,
      ),
    );
    bone.add(
      curve(
        [
          [x(0.012), -0.1, 0.08],
          [x(0.035), -0.118, 0.055],
          [x(0.058), -0.128, 0.01],
        ],
        0.0065,
        10,
      ),
    );
    bone.add(
      curve(
        [
          [x(0.082), -0.08, 0.012],
          [x(0.072), -0.108, 0.0],
          [x(0.058), -0.128, 0.004],
        ],
        0.0105,
        10,
      ),
    );
    bone.add(ball([x(0.058), -0.13, 0.0], 0.012));
  }
  bone.add(ball([0, -0.09, 0.085], 0.012, [0.9, 1.5, 0.7]));

  return bone;
}

/**
 * The thigh bone: the ball on its angled neck that goes into the hip, the
 * trochanters either side, a shaft that runs in towards the knee, and two
 * condyles side by side at the bottom.
 */
function femur(): Pieces {
  const bone = new Pieces();
  bone.add(ball([-0.025, 0.19, 0.02], 0.023));
  bone.add(rod([-0.024, 0.188, 0.019], [0.012, 0.162, 0.006], 0.013));
  bone.add(ball([0.03, 0.172, -0.004], 0.017, [1, 1.35, 1.1]));
  bone.add(ball([-0.006, 0.128, -0.012], 0.009));
  bone.add(shaft([0.018, 0.165, 0.0], [-0.018, -0.18, 0.006], 0.0115, 1.45));
  for (const x of [-0.04, 0.004]) bone.add(ball([x, -0.198, 0.004], 0.02, [1, 1, 1.3]));
  return bone;
}

/** Shin and calf together, and the kneecap in front of the knee. */
function lowerLeg(): Pieces {
  const bone = new Pieces();
  bone.add(disc([0, 0.19, 0], 0.036, 0.02, [1, 1, 0.75]));
  bone.add(ball([0, 0.16, 0.022], 0.009, [1.2, 1.6, 0.8]));
  bone.add(shaft([0, 0.18, 0], [0, -0.175, 0.005], 0.012, 1.7));
  bone.add(ball([-0.013, -0.196, 0.005], 0.011, [1, 1.3, 1]));
  bone.add(ball([0, 0.225, 0.05], 0.02, [1, 1.25, 0.55]));
  bone.add(ball([0.031, 0.168, -0.01], 0.01));
  bone.add(shaft([0.031, 0.165, -0.01], [0.033, -0.19, -0.005], 0.0055, 1.4));
  bone.add(ball([0.033, -0.204, -0.005], 0.01, [1, 1.3, 1]));
  return bone;
}

/** Upper arm: head into the shoulder, shaft, and the hinge at the elbow. */
function humerus(): Pieces {
  const bone = new Pieces();
  bone.add(ball([-0.012, 0.155, -0.005], 0.024));
  bone.add(ball([0.01, 0.148, 0.008], 0.013));
  bone.add(shaft([0.0, 0.14, 0.0], [0.004, -0.14, 0.0], 0.0105, 1.4));
  bone.add(ball([-0.025, -0.148, -0.004], 0.011));
  bone.add(ball([0.02, -0.146, 0.0], 0.008));
  bone.add(rod([-0.016, -0.158, 0.006], [0.014, -0.156, 0.006], 0.011));
  return bone;
}

/**
 * Forearm, palm forward: the ulna on the little-finger side with the point of
 * the elbow at its top, the radius on the thumb side, widening at the wrist.
 */
function forearm(): Pieces {
  const bone = new Pieces();
  bone.add(ball([-0.012, 0.12, -0.012], 0.012, [1, 1.2, 1]));
  bone.add(shaft([-0.012, 0.11, -0.005], [-0.014, -0.115, 0.0], 0.0072, 1.4));
  bone.add(ball([-0.014, -0.118, 0.0], 0.008));
  bone.add(disc([0.012, 0.105, 0.002], 0.01, 0.009));
  bone.add(shaft([0.012, 0.1, 0.002], [0.015, -0.108, 0.004], 0.0072, 1.6));
  bone.add(ball([0.016, -0.118, 0.004], 0.013, [1.1, 1, 0.9]));
  return bone;
}

/**
 * A hand, palm forward and fingers down: two rows of wrist bones, five
 * metacarpals, three bones to each finger and two to the thumb, the middle
 * finger longest.
 */
function hand(): Pieces {
  const bone = new Pieces();
  for (const [x, y] of [
    [-0.016, 0.082],
    [-0.005, 0.083],
    [0.006, 0.083],
    [0.016, 0.081],
    [-0.017, 0.068],
    [-0.006, 0.069],
    [0.005, 0.069],
    [0.017, 0.067],
  ] as const) {
    bone.add(ball([x, y, 0], 0.0075, [1, 0.85, 0.9]));
  }

  // Index to little finger. The thumb side is lateral (+x).
  const fingers = [
    { x: 0.015, meta: 0.062, lengths: [0.038, 0.023, 0.017] },
    { x: 0.004, meta: 0.064, lengths: [0.041, 0.026, 0.018] },
    { x: -0.007, meta: 0.058, lengths: [0.038, 0.024, 0.017] },
    { x: -0.017, meta: 0.052, lengths: [0.031, 0.019, 0.015] },
  ];
  for (const finger of fingers) {
    let from: V3 = [finger.x * 0.8, 0.06, 0];
    let to: V3 = [finger.x * 1.15, 0.06 - finger.meta, 0.004];
    bone.add(rod(from, to, 0.0042));
    for (const [i, length] of finger.lengths.entries()) {
      from = to;
      // A slight curl forward, as a relaxed hand has.
      to = [finger.x * 1.2, from[1] - length, from[2] + 0.002 + i * 0.002];
      bone.add(rod(from, to, 0.0038 - i * 0.0004));
    }
  }

  const thumb: V3[] = [
    [0.021, 0.064, 0.006],
    [0.034, 0.033, 0.02],
    [0.042, 0.008, 0.03],
    [0.047, -0.01, 0.036],
  ];
  for (let i = 0; i + 1 < thumb.length; i++)
    bone.add(rod(thumb[i]!, thumb[i + 1]!, 0.0048 - i * 0.0004));
  return bone;
}

/**
 * A foot: the heel and the ankle bone, the small bones of the midfoot, five
 * metatarsals fanned forward with the big toe's the stoutest, and the toes --
 * held up in an arch rather than laid flat.
 */
function foot(): Pieces {
  const bone = new Pieces();
  bone.add(ball([0, 0.028, -0.04], 0.02, [1, 0.8, 1.3]));
  bone.add(rod([0, -0.012, -0.098], [0.004, -0.004, -0.035], 0.017));
  for (const [x, y, z] of [
    [-0.012, 0.014, -0.005],
    [0.016, 0.004, -0.004],
    [-0.016, 0.01, 0.018],
    [-0.004, 0.012, 0.02],
    [0.008, 0.01, 0.021],
  ] as const) {
    bone.add(ball([x, y, z], 0.0095));
  }

  const toes = [
    { x: -0.023, r: 0.0068, lengths: [0.027, 0.021] },
    { x: -0.01, r: 0.0046, lengths: [0.02, 0.012, 0.009] },
    { x: 0.002, r: 0.0044, lengths: [0.018, 0.011, 0.008] },
    { x: 0.014, r: 0.0042, lengths: [0.016, 0.01, 0.008] },
    { x: 0.026, r: 0.004, lengths: [0.014, 0.009, 0.007] },
  ];
  for (const [i, toe] of toes.entries()) {
    let from: V3 = [toe.x * 0.7, 0.008, 0.03];
    let to: V3 = [toe.x, -0.022, 0.1 - i * 0.006];
    bone.add(rod(from, to, toe.r));
    for (const length of toe.lengths) {
      from = to;
      to = [toe.x * 1.02, -0.026, from[2] + length];
      bone.add(rod(from, to, toe.r * 0.82));
    }
  }
  return bone;
}

/**
 * The shoulder girdle: the collarbone with its lazy S, from the top of the
 * sternum out to the point of the shoulder, and the shoulder blade on the
 * back -- a triangular plate with the ridge of its spine running out to meet
 * the collarbone. An arm has to hang from something.
 */
function shoulder(): Pieces {
  const bone = new Pieces();
  bone.add(
    curve(
      [
        [-0.085, 0.0, 0.09],
        [-0.03, 0.012, 0.075],
        [0.03, 0.013, 0.03],
        [0.075, 0.015, -0.005],
      ],
      0.0065,
    ),
  );
  bone.add(ball([-0.086, 0.0, 0.09], 0.009));

  // The blade, lying against the back of the ribs.
  bone.add(ball([0.005, -0.045, -0.062], 0.085, [0.62, 1.0, 0.1], [0, 0.45, 0]));
  bone.add(
    curve(
      [
        [-0.03, 0.02, -0.078],
        [0.02, 0.022, -0.07],
        [0.078, 0.018, -0.022],
      ],
      0.0065,
      10,
    ),
  );
  bone.add(ball([0.078, 0.016, -0.02], 0.012, [1.2, 0.6, 1.2]));
  bone.add(ball([0.07, -0.022, -0.03], 0.013, [0.6, 1.2, 1]));
  return bone;
}

/** Everything in the lesson that has a left and a right. */
const PAIRED = new Set(['femur', 'tibia', 'humerus', 'forearm', 'hand', 'foot', 'shoulder']);

/**
 * Builds one bone by name.
 *
 * An unknown name falls back to a plain long bone rather than throwing: a
 * lesson naming a bone this file has not learned should be a bone a child can
 * still place, not a hole in the skeleton.
 */
export function buildBone({ name, object }: BuildContext): BuiltBone {
  const scale = object.radius ?? 1;
  // Right when it stands right of the midline; that is the only side authored.
  const mirrored = PAIRED.has(name) && (object.pos?.[0] ?? 0) < 0;

  switch (name) {
    case 'skull': {
      const { bone, hollows } = skull();
      return finish(bone, { hollows, mirrored: false, scale, nameplate: { side: 1, height: 0 } });
    }
    case 'spine':
      // Low down on the left, by the small of the back, away from the ribs' name.
      return finish(spine(), { mirrored: false, scale, nameplate: { side: -1, height: -0.2 } });
    case 'ribs': {
      const { bone, cartilage } = ribcage();
      return finish(bone, {
        cartilage,
        mirrored: false,
        scale,
        nameplate: { side: 1, height: 0 },
      });
    }
    case 'pelvis':
      return finish(pelvis(), { mirrored: false, scale, nameplate: { side: -1, height: 0 } });
    case 'shoulder':
      return finish(shoulder(), { mirrored, scale });
    case 'femur':
      return finish(femur(), { mirrored, scale });
    case 'tibia':
      return finish(lowerLeg(), { mirrored, scale });
    case 'humerus':
      return finish(humerus(), { mirrored, scale });
    case 'forearm':
      return finish(forearm(), { mirrored, scale });
    case 'hand':
      return finish(hand(), { mirrored, scale });
    case 'foot':
      return finish(foot(), { mirrored, scale });
    default:
      return finish(new Pieces().add(shaft([0, 0.15, 0], [0, -0.15, 0], 0.018)), {
        mirrored: false,
        scale,
      });
  }
}
